import React from 'react';
import { motion } from 'framer-motion';

/**
 * BrandBanner — Antes "NeonBanner"
 * Sección editorial oscura con acentos cobalt/magenta (estética híbrida)
 * Para versión "limpia": cambiar bg-brand-black por bg-brand-bone y los estilos de texto a brand-black
 */
const NeonBanner = () => {
  return (
    <section className="relative py-6 md:py-8 overflow-hidden bg-brand-black">
      {/* Abstract background pattern — líneas decorativas con cobalt/magenta */}
      <div className="absolute inset-0 opacity-25">
        <svg
          className="w-full h-full"
          viewBox="0 0 1920 300"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Flowing curves */}
          <path
            d="M-100,150 Q400,30 800,150 T1700,120 T2100,180"
            fill="none"
            stroke="url(#brandGradient1)"
            strokeWidth="3"
            opacity="0.6"
          />
          <path
            d="M-100,180 Q300,250 700,130 T1500,200 T2100,140"
            fill="none"
            stroke="url(#brandGradient2)"
            strokeWidth="2"
            opacity="0.5"
          />
          <path
            d="M-100,100 Q500,180 900,80 T1600,150 T2100,100"
            fill="none"
            stroke="url(#brandGradient1)"
            strokeWidth="1.5"
            opacity="0.4"
          />

          {/* Accent circles */}
          <circle cx="150" cy="60" r="80" fill="none" stroke="url(#brandGradient2)" strokeWidth="1.5" opacity="0.4" />
          <circle cx="1750" cy="240" r="100" fill="none" stroke="url(#brandGradient1)" strokeWidth="1.5" opacity="0.4" />
          <circle cx="960" cy="150" r="180" fill="none" stroke="#ffffff" strokeWidth="1" opacity="0.15" />
          <circle cx="500" cy="200" r="50" fill="none" stroke="url(#brandGradient1)" strokeWidth="1" opacity="0.3" />
          <circle cx="1400" cy="80" r="60" fill="none" stroke="url(#brandGradient2)" strokeWidth="1" opacity="0.3" />

          {/* Diagonal accent lines */}
          <line x1="50" y1="0" x2="350" y2="300" stroke="url(#brandGradient1)" strokeWidth="2" opacity="0.3" />
          <line x1="1550" y1="0" x2="1850" y2="300" stroke="url(#brandGradient2)" strokeWidth="2" opacity="0.3" />
          <line x1="700" y1="300" x2="900" y2="0" stroke="#ffffff" strokeWidth="1" opacity="0.15" />
          <line x1="1050" y1="300" x2="1250" y2="0" stroke="#ffffff" strokeWidth="1" opacity="0.15" />

          {/* Small decorative dots */}
          <circle cx="300" cy="100" r="3" fill="#1B4FD8" opacity="0.5" />
          <circle cx="600" cy="220" r="2" fill="#C2185B" opacity="0.5" />
          <circle cx="1100" cy="50" r="3" fill="#ffffff" opacity="0.3" />
          <circle cx="1350" cy="250" r="2" fill="#1B4FD8" opacity="0.5" />
          <circle cx="1650" cy="120" r="3" fill="#C2185B" opacity="0.5" />

          {/* Gradients — cobalt → magenta */}
          <defs>
            <linearGradient id="brandGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#1B4FD8" />
              <stop offset="100%" stopColor="#C2185B" />
            </linearGradient>
            <linearGradient id="brandGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#C2185B" />
              <stop offset="100%" stopColor="#1B4FD8" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Background glow effects — híbrido: cobalt + magenta suaves */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-1/2 left-1/4 w-72 h-72 -translate-y-1/2 rounded-full blur-[100px] opacity-15"
          style={{ background: 'radial-gradient(circle, #1B4FD8 0%, transparent 70%)' }}
        />
        <div
          className="absolute top-1/2 right-1/4 w-72 h-72 -translate-y-1/2 rounded-full blur-[100px] opacity-15"
          style={{ background: 'radial-gradient(circle, #C2185B 0%, transparent 70%)' }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Main headline — gradiente cobalt → white → magenta */}
          <h2
            className="text-2xl md:text-4xl lg:text-5xl font-light italic mb-2 md:mb-3 tracking-wide"
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              background: 'linear-gradient(90deg, #1B4FD8 0%, #ffffff 30%, #C2185B 60%, #ffffff 80%, #1B4FD8 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'brandShimmer 12s ease-in-out infinite',
            }}
          >
            ¿Y si duran todo el día?
          </h2>

          {/* Subtitle */}
          <p
            className="text-sm md:text-lg lg:text-xl font-light tracking-widest uppercase"
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              background: 'linear-gradient(90deg, #C2185B 0%, #ffffff 50%, #1B4FD8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '0.15em'
            }}
          >
            La calidad del original, por una fracción de su precio
          </p>
        </motion.div>
      </div>

      {/* CSS shimmer animation */}
      <style>{`
        @keyframes brandShimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>
    </section>
  );
};

export default NeonBanner;
